/**
 * User Details Page
 *
 * Displays detailed information about a specific platform user.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  User,
  Mail,
  Phone,
  Calendar,
  Shield,
  ArrowLeft,
  Edit,
  Ban,
  CheckCircle,
  CheckCircle2,
  XCircle,
  Clock,
  UserCheck,
  CreditCard,
  Check,
  Loader2,
  Package,
  Users as UsersIcon,
  Briefcase,
  MessageSquare,
  Smartphone,
  FileText,
  History,
} from 'lucide-react';

import {
  Button,
  Badge,
  Card,
  CardBody,
  Loader,
  Modal,
  ConfirmationDialog,
  AuditTimeline,
} from '@/components/common';
import { UserRolesPanel } from '@/components/users/UserRolesPanel';
import { userService, organizationService, subscriptionService, auditService } from '@/api/services';
import type { UserDetails } from '@/api/types';
import { formatDate, formatDateTime } from '@/utils/date.utils';
import { useToast } from '@/hooks/useToast';
import { useCan } from '@/hooks/usePermissions';

// ============================================
// Verification Pill Component
// ============================================

function VerificationPill({ label, verified }: { label: string; verified?: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ${
        verified
          ? 'bg-success/10 text-success'
          : 'bg-secondary-100 text-foreground-muted dark:bg-secondary-800/50 dark:text-foreground-dark-muted'
      }`}
    >
      {verified ? (
        <CheckCircle2 className="h-3.5 w-3.5" />
      ) : (
        <XCircle className="h-3.5 w-3.5" />
      )}
      {label} {verified ? 'Verified' : 'Not Verified'}
    </span>
  );
}

// ============================================
// Manage Plans Modal
// ============================================

interface AllowedPlan {
  planCode: string;
  planName: string;
  maxEmployees?: number;
  maxContracts?: number;
  maxUsers?: number;
  maxActiveContracts?: number;
  smsFeatureEnabled?: boolean;
  mobileAppFeatureEnabled?: boolean;
  eInvoiceEnabled?: boolean;
}

interface SubscriptionPlanOption {
  code: string;
  name: string;
  maxEmployees?: number;
  maxContracts?: number;
  maxActiveContracts?: number;
  maxUsers?: number;
  smsFeatureEnabled?: boolean;
  mobileAppFeatureEnabled?: boolean;
  eInvoiceEnabled?: boolean;
}

interface ManagePlansModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  onSuccess: () => void;
  initialPlans: SubscriptionPlanOption[];
  initialAssigned: Set<string>;
}

function ManagePlansModal({ open, onOpenChange, userId, onSuccess, initialPlans, initialAssigned }: ManagePlansModalProps) {
  const toast = useToast();
  const [allPlans] = useState<SubscriptionPlanOption[]>(initialPlans);
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set(initialAssigned));
  const [alreadyAssigned] = useState<Set<string>>(initialAssigned);
  const [isSaving, setIsSaving] = useState(false);

  const togglePlan = (code: string) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Compute newly added plans (selected but not previously assigned)
      const addedPlans = Array.from(selectedCodes).filter((code) => !alreadyAssigned.has(code));
      // Compute removed plans (previously assigned but now deselected)
      const removedPlans = Array.from(alreadyAssigned).filter((code) => !selectedCodes.has(code));

      await organizationService.createAllowedPlansForUser({
        allowedPlanCodeList: addedPlans,
        removePlanCodeList: removedPlans,
        platromUserId: userId,
      });
      toast.success('Plans updated successfully');
      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      toast.error(error?.showMessage || 'Failed to update plans');
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = (() => {
    if (selectedCodes.size !== alreadyAssigned.size) return true;
    for (const code of selectedCodes) {
      if (!alreadyAssigned.has(code)) return true;
    }
    return false;
  })();

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Manage Allowed Plans"
      description="Select which subscription plans this user can assign to organizations"
      size="full"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={isSaving}
            disabled={!hasChanges}
          >
            Save Changes
          </Button>
        </>
      }
    >
      {allPlans.length === 0 ? (
        <div className="text-center py-12">
          <Package className="mx-auto h-10 w-10 text-foreground-muted dark:text-foreground-dark-muted" />
          <p className="mt-2 text-sm text-foreground-muted dark:text-foreground-dark-muted">
            No subscription plans available
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-foreground-muted dark:text-foreground-dark-muted">
              {selectedCodes.size} of {allPlans.length} plans selected
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSelectedCodes(new Set(allPlans.map((p) => p.code)))}
                className="text-xs font-medium text-primary hover:text-primary-700"
              >
                Select All
              </button>
              <button
                type="button"
                onClick={() => setSelectedCodes(new Set())}
                className="text-xs font-medium text-foreground-muted hover:text-foreground dark:hover:text-foreground-dark"
              >
                Clear
              </button>
            </div>
          </div>
          {allPlans.map((plan) => {
            const isSelected = selectedCodes.has(plan.code);
            const isAssigned = alreadyAssigned.has(plan.code);
            return (
              <button
                key={plan.code}
                type="button"
                onClick={() => togglePlan(plan.code)}
                className={`w-full rounded-xl border-2 text-left transition-all ${
                  isSelected
                    ? 'border-primary bg-primary-50/50 dark:bg-primary-900/15 shadow-sm'
                    : 'border-border dark:border-border-dark hover:border-primary/30 hover:bg-secondary-50/50 dark:hover:bg-secondary-900/20'
                }`}
              >
                {/* Top row: checkbox + name + badges */}
                <div className="flex items-center gap-3 px-4 pt-3 pb-2">
                  <div
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors ${
                      isSelected
                        ? 'bg-primary border-primary text-white'
                        : 'border-secondary-300 dark:border-secondary-600'
                    }`}
                  >
                    {isSelected && <Check className="h-3.5 w-3.5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-foreground dark:text-foreground-dark">
                        {plan.name}
                      </p>
                      <span className="text-2xs font-mono text-foreground-muted dark:text-foreground-dark-muted bg-secondary-100 dark:bg-secondary-800 rounded px-1.5 py-0.5">
                        {plan.code}
                      </span>
                    </div>
                  </div>
                  {isAssigned && (
                    <Badge variant="success" dot>Assigned</Badge>
                  )}
                </div>

                {/* Stats row */}
                <div className="flex items-center gap-4 px-4 pb-2 pl-12 flex-wrap">
                  {plan.maxEmployees !== undefined && (
                    <span className="flex items-center gap-1.5 text-xs text-foreground-muted dark:text-foreground-dark-muted">
                      <UsersIcon className="h-3.5 w-3.5 text-primary" />
                      <span className="font-semibold text-foreground dark:text-foreground-dark">{plan.maxEmployees}</span> Employees
                    </span>
                  )}
                  {plan.maxContracts !== undefined && (
                    <span className="flex items-center gap-1.5 text-xs text-foreground-muted dark:text-foreground-dark-muted">
                      <Briefcase className="h-3.5 w-3.5 text-warning" />
                      <span className="font-semibold text-foreground dark:text-foreground-dark">{plan.maxContracts}</span> Contracts
                    </span>
                  )}
                  {plan.maxActiveContracts !== undefined && (
                    <span className="flex items-center gap-1.5 text-xs text-foreground-muted dark:text-foreground-dark-muted">
                      <FileText className="h-3.5 w-3.5 text-success" />
                      <span className="font-semibold text-foreground dark:text-foreground-dark">{plan.maxActiveContracts}</span> Active
                    </span>
                  )}
                  {plan.maxUsers !== undefined && (
                    <span className="flex items-center gap-1.5 text-xs text-foreground-muted dark:text-foreground-dark-muted">
                      <UserCheck className="h-3.5 w-3.5 text-primary" />
                      <span className="font-semibold text-foreground dark:text-foreground-dark">{plan.maxUsers}</span> Users
                    </span>
                  )}
                </div>

                {/* Features row */}
                {(plan.smsFeatureEnabled || plan.mobileAppFeatureEnabled || plan.eInvoiceEnabled) && (
                  <div className="flex items-center gap-1.5 px-4 pb-3 pl-12 flex-wrap">
                    {plan.smsFeatureEnabled && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-2xs font-medium text-success">
                        <MessageSquare className="h-3 w-3" /> SMS
                      </span>
                    )}
                    {plan.mobileAppFeatureEnabled && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-2xs font-medium text-primary">
                        <Smartphone className="h-3 w-3" /> Mobile App
                      </span>
                    )}
                    {plan.eInvoiceEnabled && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 text-2xs font-medium text-warning">
                        <FileText className="h-3 w-3" /> E-Invoice
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

// ============================================
// Page Component
// ============================================

export function UserDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const canEdit = useCan('orgUser.user.manage');
  const canManageAllowedPlans = useCan('orgPlan.plan.manage');

  // State
  const [user, setUser] = useState<UserDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [allowedPlans, setAllowedPlans] = useState<AllowedPlan[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [isLoadingModalData, setIsLoadingModalData] = useState(false);
  const [modalPlans, setModalPlans] = useState<SubscriptionPlanOption[]>([]);
  const [modalAssigned, setModalAssigned] = useState<Set<string>>(new Set());
  const [auditOpen, setAuditOpen] = useState(false);

  const fetchAuditLogs = useCallback(
    (page: number, pageSize: number) => {
      return auditService.getAuditLogs(user!.auditId!, page, pageSize);
    },
    [user]
  );

  // Fetch allowed plans with full details
  const fetchAllowedPlans = useCallback(async () => {
    if (!id) return;
    setIsLoadingPlans(true);
    try {
      const [allowedRaw, allPlansList] = await Promise.all([
        organizationService.getAllowedPlansByUserId(id),
        subscriptionService.getSubscriptionPlanList(),
      ]);

      // Build a map of full plan details keyed by code
      const planDetailsMap = new Map<string, any>();
      for (const item of allPlansList) {
        const plan = item.subscriptionPlan || item;
        if (plan.code) planDetailsMap.set(plan.code, plan);
      }

      setAllowedPlans(
        allowedRaw.map((p: any) => {
          const code = p.planCode || p.code || p;
          const full = planDetailsMap.get(code);
          return {
            planCode: code,
            planName: full?.name || p.planName || p.name || code,
            maxEmployees: full?.maxEmployees,
            maxContracts: full?.maxContracts,
            maxUsers: full?.maxUsers,
            maxActiveContracts: full?.maxActiveContracts,
            smsFeatureEnabled: full?.smsFeatureEnabled,
            mobileAppFeatureEnabled: full?.mobileAppFeatureEnabled,
            eInvoiceEnabled: full?.eInvoiceEnabled,
          };
        })
      );
    } catch {
      // Silently fail — plans section just shows empty
    } finally {
      setIsLoadingPlans(false);
    }
  }, [id]);

  // Pre-fetch modal data, then open modal
  const handleOpenManagePlans = useCallback(async () => {
    if (!id) return;
    setIsLoadingModalData(true);
    try {
      const [planList, allowedRaw] = await Promise.all([
        subscriptionService.getSubscriptionPlanList(),
        organizationService.getAllowedPlansByUserId(id),
      ]);

      const planMap = new Map<string, SubscriptionPlanOption>();
      for (const item of planList) {
        const plan = item.subscriptionPlan || item;
        if (plan.code && plan.name && plan.isActive !== false) {
          planMap.set(plan.code, {
            code: plan.code,
            name: plan.name,
            maxEmployees: plan.maxEmployees,
            maxContracts: plan.maxContracts,
            maxActiveContracts: plan.maxActiveContracts,
            maxUsers: plan.maxUsers,
            smsFeatureEnabled: plan.smsFeatureEnabled,
            mobileAppFeatureEnabled: plan.mobileAppFeatureEnabled,
            eInvoiceEnabled: plan.eInvoiceEnabled,
          });
        }
      }
      setModalPlans(Array.from(planMap.values()));

      const assignedCodes = new Set<string>();
      for (const p of allowedRaw) {
        const code = p.planCode || p.code || p;
        if (typeof code === 'string') assignedCodes.add(code);
      }
      setModalAssigned(assignedCodes);

      setShowPlansModal(true);
    } catch {
      toast.error('Failed to load plans');
    } finally {
      setIsLoadingModalData(false);
    }
  }, [id, toast]);

  // Fetch data
  useEffect(() => {
    if (!id) return;

    const fetchUser = async () => {
      setIsLoading(true);
      try {
        const userData = await userService.getUserById(id);
        setUser(userData);
      } catch (error) {
        console.error('Failed to fetch user:', error);
        toast.error('Failed to load user details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
    fetchAllowedPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Handle status toggle
  const handleStatusToggle = async () => {
    if (!user || !id) return;

    setIsUpdatingStatus(true);
    try {
      const newStatus = user.userStatus === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
      await userService.updateUser({ id, userStatus: newStatus });
      setUser({ ...user, userStatus: newStatus });
      toast.success(`User ${newStatus === 'ACTIVE' ? 'activated' : 'suspended'} successfully`);
    } catch (error) {
      console.error('Failed to update user status:', error);
      toast.error('Failed to update user status');
    } finally {
      setIsUpdatingStatus(false);
      setShowStatusDialog(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader size="lg" message="Loading user details..." />
      </div>
    );
  }

  // Not found state
  if (!user) {
    return (
      <div className="text-center py-16">
        <User className="mx-auto h-12 w-12 text-foreground-muted dark:text-foreground-dark-muted" />
        <h2 className="mt-4 text-lg font-semibold text-foreground dark:text-foreground-dark">
          User not found
        </h2>
        <p className="mt-2 text-foreground-muted dark:text-foreground-dark-muted">
          The user you are looking for does not exist.
        </p>
        <Button className="mt-4" onClick={() => navigate('/users')}>
          Back to Users
        </Button>
      </div>
    );
  }

  const statusVariant =
    user.userStatus === 'ACTIVE'
      ? 'success'
      : user.userStatus === 'SUSPENDED'
        ? 'warning'
        : user.userStatus === 'TERMINATED'
          ? 'danger'
          : 'default';

  const canChangeStatus = canEdit;

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/users')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground dark:text-foreground-dark">
              User Details
            </h1>
            <p className="mt-0.5 text-sm text-foreground-muted dark:text-foreground-dark-muted">
              View and manage user information
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {user.auditId && (
            <Button
              variant="outline"
              onClick={() => setAuditOpen(true)}
              leftIcon={<History className="h-4 w-4" />}
            >
              Audit
            </Button>
          )}
          {canChangeStatus && user.userStatus !== 'TERMINATED' && (
            <Button
              variant="outline"
              onClick={() => setShowStatusDialog(true)}
              leftIcon={
                user.userStatus === 'ACTIVE' ? (
                  <Ban className="h-4 w-4" />
                ) : (
                  <CheckCircle className="h-4 w-4" />
                )
              }
            >
              {user.userStatus === 'ACTIVE' ? 'Suspend' : 'Activate'}
            </Button>
          )}
          {canEdit && (
            <Button asChild leftIcon={<Edit className="h-4 w-4" />}>
              <Link to={`/users/${id}/edit`}>Edit User</Link>
            </Button>
          )}
        </div>
      </div>

      {/* User Overview Card */}
      <Card>
        <CardBody className="p-0">
          {/* Top section: Avatar + Name + Status */}
          <div className="flex items-center gap-5 p-6 pb-5">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-primary/10">
              {user.profileImage ? (
                <img
                  src={user.profileImage}
                  alt={user.username}
                  className="h-14 w-14 rounded-xl object-cover"
                />
              ) : (
                <User className="h-7 w-7 text-primary" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-xl font-bold text-foreground dark:text-foreground-dark truncate">
                  {fullName}
                </h2>
                <Badge variant={statusVariant} dot>
                  {user.userStatus}
                </Badge>
              </div>
              <p className="mt-0.5 text-sm text-foreground-muted dark:text-foreground-dark-muted">
                @{user.username}
              </p>
            </div>
          </div>

          {/* Details grid */}
          <div className="grid grid-cols-2 gap-px border-t border-border dark:border-border-dark bg-border dark:bg-border-dark lg:grid-cols-4">
            <div className="bg-card dark:bg-card-dark p-4">
              <div className="flex items-center gap-2 text-foreground-muted dark:text-foreground-dark-muted">
                <Mail className="h-4 w-4" />
                <span className="text-xs font-medium uppercase tracking-wide">Email</span>
              </div>
              <p className="mt-1.5 text-sm font-semibold text-foreground dark:text-foreground-dark truncate" title={user.email}>
                {user.email}
              </p>
            </div>
            <div className="bg-card dark:bg-card-dark p-4">
              <div className="flex items-center gap-2 text-foreground-muted dark:text-foreground-dark-muted">
                <Phone className="h-4 w-4" />
                <span className="text-xs font-medium uppercase tracking-wide">Mobile</span>
              </div>
              <p className="mt-1.5 text-sm font-semibold text-foreground dark:text-foreground-dark">
                {user.mobile || 'N/A'}
              </p>
            </div>
            <div className="bg-card dark:bg-card-dark p-4">
              <div className="flex items-center gap-2 text-foreground-muted dark:text-foreground-dark-muted">
                <Calendar className="h-4 w-4" />
                <span className="text-xs font-medium uppercase tracking-wide">Created</span>
              </div>
              <p className="mt-1.5 text-sm font-semibold text-foreground dark:text-foreground-dark">
                {formatDate(user.createdDate)}
              </p>
            </div>
            <div className="bg-card dark:bg-card-dark p-4">
              <div className="flex items-center gap-2 text-foreground-muted dark:text-foreground-dark-muted">
                <Clock className="h-4 w-4" />
                <span className="text-xs font-medium uppercase tracking-wide">Last Login</span>
              </div>
              <p className="mt-1.5 text-sm font-semibold text-foreground dark:text-foreground-dark">
                {user.lastLoginDate ? formatDateTime(user.lastLoginDate) : 'Never'}
              </p>
            </div>
          </div>

          {/* Roles & Verification */}
          <div className="border-t border-border dark:border-border-dark px-6 py-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              {/* Roles */}
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-foreground-muted dark:text-foreground-dark-muted mb-2">
                  Roles
                </p>
                <div className="flex flex-wrap gap-2">
                  {user.listOfRoles?.length ? (
                    user.listOfRoles.map((role) => (
                      <Badge key={role} variant="primary">
                        {role.replace('ROLE_', '')}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-sm text-foreground-muted dark:text-foreground-dark-muted">No roles assigned</span>
                  )}
                </div>
              </div>

              {/* Verification status */}
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-foreground-muted dark:text-foreground-dark-muted mb-2">
                  Verification
                </p>
                <div className="flex flex-wrap gap-2">
                  <VerificationPill label="Email" verified={user.emailVerified} />
                  <VerificationPill label="Mobile" verified={user.mobileVerified} />
                </div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Additional Info Card */}
      <Card>
        <CardBody className="p-0">
          <div className="grid grid-cols-2 gap-px bg-border dark:bg-border-dark lg:grid-cols-4">
            <div className="bg-card dark:bg-card-dark p-4">
              <div className="flex items-center gap-2 text-foreground-muted dark:text-foreground-dark-muted">
                <Briefcase className="h-4 w-4" />
                <span className="text-xs font-medium uppercase tracking-wide">User Type</span>
              </div>
              <p className="mt-1.5 text-sm font-semibold text-foreground dark:text-foreground-dark">
                {user.orgUserType || 'N/A'}
              </p>
            </div>
            <div className="bg-card dark:bg-card-dark p-4">
              <div className="flex items-center gap-2 text-foreground-muted dark:text-foreground-dark-muted">
                <UserCheck className="h-4 w-4" />
                <span className="text-xs font-medium uppercase tracking-wide">Gender</span>
              </div>
              <p className="mt-1.5 text-sm font-semibold text-foreground dark:text-foreground-dark">
                {user.gender || 'N/A'}
              </p>
            </div>
            <div className="bg-card dark:bg-card-dark p-4">
              <div className="flex items-center gap-2 text-foreground-muted dark:text-foreground-dark-muted">
                <Clock className="h-4 w-4" />
                <span className="text-xs font-medium uppercase tracking-wide">Last Login</span>
              </div>
              <p className="mt-1.5 text-sm font-semibold text-foreground dark:text-foreground-dark">
                {user.lastLoginDate ? formatDateTime(user.lastLoginDate) : 'Never'}
              </p>
            </div>
            <div className="bg-card dark:bg-card-dark p-4">
              <div className="flex items-center gap-2 text-foreground-muted dark:text-foreground-dark-muted">
                <Shield className="h-4 w-4" />
                <span className="text-xs font-medium uppercase tracking-wide">Last Updated</span>
              </div>
              <p className="mt-1.5 text-sm font-semibold text-foreground dark:text-foreground-dark">
                {user.updatedDate ? formatDateTime(user.updatedDate) : 'N/A'}
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Allowed Plans Section — requires plan-manage permission */}
      {canManageAllowedPlans && <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary">
              <CreditCard className="h-4 w-4" />
            </div>
            <h3 className="text-base font-semibold text-foreground dark:text-foreground-dark">
              Allowed Subscription Plans
            </h3>
            {allowedPlans.length > 0 && (
              <Badge variant="primary">{allowedPlans.length}</Badge>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<CreditCard className="h-3.5 w-3.5" />}
            onClick={handleOpenManagePlans}
            loading={isLoadingModalData}
          >
            Manage Plans
          </Button>
        </div>

        {isLoadingPlans ? (
          <Card>
            <CardBody>
              <div className="flex items-center gap-2 py-8 justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                <span className="text-sm text-foreground-muted dark:text-foreground-dark-muted">Loading plans...</span>
              </div>
            </CardBody>
          </Card>
        ) : allowedPlans.length === 0 ? (
          <Card>
            <CardBody>
              <div className="text-center py-8">
                <Package className="mx-auto h-10 w-10 text-foreground-muted dark:text-foreground-dark-muted" />
                <p className="mt-2 text-sm font-medium text-foreground-muted dark:text-foreground-dark-muted">
                  No plans assigned to this user
                </p>
                <p className="mt-1 text-xs text-foreground-muted dark:text-foreground-dark-muted">
                  Click "Manage Plans" to assign subscription plans
                </p>
              </div>
            </CardBody>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {allowedPlans.map((plan) => (
              <Card key={plan.planCode} className="overflow-hidden hover:shadow-md transition-shadow">
                <CardBody className="p-0">
                  {/* Plan header */}
                  <div className="bg-gradient-to-r from-primary-500 to-primary-400 px-4 py-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-bold text-white">{plan.planName}</h4>
                      <span className="rounded-full bg-white/20 px-2 py-0.5 text-2xs font-medium text-white">
                        {plan.planCode}
                      </span>
                    </div>
                  </div>

                  {/* Plan stats */}
                  <div className="px-4 py-3 space-y-2.5">
                    {plan.maxEmployees !== undefined && (
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-2 text-xs text-foreground-muted dark:text-foreground-dark-muted">
                          <UsersIcon className="h-3.5 w-3.5" />
                          Max Employees
                        </span>
                        <span className="text-xs font-semibold text-foreground dark:text-foreground-dark">
                          {plan.maxEmployees}
                        </span>
                      </div>
                    )}
                    {plan.maxContracts !== undefined && (
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-2 text-xs text-foreground-muted dark:text-foreground-dark-muted">
                          <Briefcase className="h-3.5 w-3.5" />
                          Max Contracts
                        </span>
                        <span className="text-xs font-semibold text-foreground dark:text-foreground-dark">
                          {plan.maxContracts}
                        </span>
                      </div>
                    )}
                    {plan.maxActiveContracts !== undefined && (
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-2 text-xs text-foreground-muted dark:text-foreground-dark-muted">
                          <FileText className="h-3.5 w-3.5" />
                          Active Contracts
                        </span>
                        <span className="text-xs font-semibold text-foreground dark:text-foreground-dark">
                          {plan.maxActiveContracts}
                        </span>
                      </div>
                    )}
                    {plan.maxUsers !== undefined && (
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-2 text-xs text-foreground-muted dark:text-foreground-dark-muted">
                          <UserCheck className="h-3.5 w-3.5" />
                          Max Users
                        </span>
                        <span className="text-xs font-semibold text-foreground dark:text-foreground-dark">
                          {plan.maxUsers}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Plan features */}
                  {(plan.smsFeatureEnabled !== undefined ||
                    plan.mobileAppFeatureEnabled !== undefined ||
                    plan.eInvoiceEnabled !== undefined) && (
                    <div className="border-t border-border dark:border-border-dark px-4 py-2.5">
                      <div className="flex flex-wrap gap-1.5">
                        {plan.smsFeatureEnabled && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-2xs font-medium text-success">
                            <MessageSquare className="h-3 w-3" />
                            SMS
                          </span>
                        )}
                        {plan.mobileAppFeatureEnabled && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-2xs font-medium text-primary">
                            <Smartphone className="h-3 w-3" />
                            Mobile App
                          </span>
                        )}
                        {plan.eInvoiceEnabled && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 text-2xs font-medium text-warning">
                            <FileText className="h-3 w-3" />
                            E-Invoice
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            ))}
          </div>
        )}
      </div>}

      {/* Roles & Permissions (dynamic RBAC) */}
      {id && <UserRolesPanel userId={id} />}

      {/* Manage Plans Modal */}
      {canManageAllowedPlans && id && showPlansModal && (
        <ManagePlansModal
          open={showPlansModal}
          onOpenChange={setShowPlansModal}
          userId={id}
          onSuccess={fetchAllowedPlans}
          initialPlans={modalPlans}
          initialAssigned={modalAssigned}
        />
      )}

      {/* Status Change Confirmation Dialog */}
      <ConfirmationDialog
        open={showStatusDialog}
        onOpenChange={setShowStatusDialog}
        title={user.userStatus === 'ACTIVE' ? 'Suspend User' : 'Activate User'}
        message={
          user.userStatus === 'ACTIVE'
            ? `Are you sure you want to suspend ${fullName}? They will no longer be able to access the system.`
            : `Are you sure you want to activate ${fullName}? They will regain access to the system.`
        }
        variant={user.userStatus === 'ACTIVE' ? 'warning' : 'default'}
        confirmText={user.userStatus === 'ACTIVE' ? 'Suspend' : 'Activate'}
        onConfirm={handleStatusToggle}
        isLoading={isUpdatingStatus}
      />

      {/* Audit Timeline */}
      {user.auditId && (
        <AuditTimeline
          open={auditOpen}
          onClose={() => setAuditOpen(false)}
          title={`${fullName} Audit Logs`}
          fetchLogs={fetchAuditLogs}
        />
      )}
    </div>
  );
}

export default UserDetailsPage;
